<template>
  <main class="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
    <header class="flex flex-none items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
      <RouterLink :to="browseRoute" class="text-sm font-semibold text-primary hover:underline">Back to section</RouterLink>
      <span class="text-gray-300">|</span>
      <strong class="truncate text-sm text-gray-700">{{ sectionKey }} / {{ type }} / {{ displayQualifier }}</strong>
      <span class="flex-1" />
      <span class="text-xs text-gray-400">{{ versions.length }} versions</span>
    </header>

    <div class="flex flex-none flex-wrap items-center gap-4 border-b border-gray-200 bg-white px-4 py-3">
      <label class="flex items-center gap-2 text-xs font-bold uppercase text-gray-500">
        Version A
        <select v-model="selectedA" class="rounded border border-gray-300 px-2 py-2 text-sm font-normal normal-case">
          <option value="">Choose</option>
          <option v-for="version in versions" :key="version.versionId" :value="version.versionId">{{ version.label }}</option>
        </select>
      </label>
      <label class="flex items-center gap-2 text-xs font-bold uppercase text-gray-500">
        Version B
        <select v-model="selectedB" class="rounded border border-gray-300 px-2 py-2 text-sm font-normal normal-case">
          <option value="">Choose</option>
          <option v-for="version in versions" :key="version.versionId" :value="version.versionId">{{ version.label }}</option>
        </select>
      </label>
      <button
        type="button"
        :disabled="!canCompare || comparing"
        class="!m-0 !rounded !bg-primary !px-4 !py-2 !text-sm !font-semibold !text-white disabled:opacity-50"
        @click="compare"
      >
        {{ comparing ? "Loading..." : "Compare" }}
      </button>
    </div>

    <p v-if="error" class="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">{{ error }}</p>
    <div v-if="loadingVersions" class="flex flex-1 items-center justify-center text-sm text-gray-500">Loading versions...</div>
    <div v-else-if="result" class="flex min-h-0 flex-1 overflow-hidden">
      <div class="min-w-0 flex-1 border-r border-gray-200"><ComparePanel :section="result.a" :diff="diff" side="a" /></div>
      <div class="min-w-0 flex-1"><ComparePanel :section="result.b" :diff="diff" side="b" /></div>
    </div>
    <div v-else class="flex flex-1 items-center justify-center text-sm text-gray-500">Select two versions to compare.</div>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { RouterLink, useRoute } from "vue-router";

import ComparePanel from "../components/ComparePanel.vue";
import { compareRegSections, getSectionVersions } from "../api.js";
import { buildDiff } from "../utils/diff.js";

const route = useRoute();
const sectionKey = String(route.params.sectionKey);
const type = String(route.params.type);
const qualifier = route.params.qualifier === "_" ? "" : String(route.params.qualifier);
const displayQualifier = qualifier || "unqualified";
const versions = ref([]);
const selectedA = ref("");
const selectedB = ref("");
const loadingVersions = ref(false);
const comparing = ref(false);
const result = ref(null);
const diff = ref(null);
const error = ref("");
const canCompare = computed(() => selectedA.value && selectedB.value && selectedA.value !== selectedB.value);
const partNumber = route.query.partNumber === "23" ? "23" : "25";
const browseRoute = computed(() => ({
  name: `CS${partNumber}`,
  query: {
    amendment: route.query.amendment || (partNumber === "23" ? "6" : "27"),
    ...(route.query.versionId ? { versionId: route.query.versionId } : {}),
  },
}));

onMounted(async () => {
  loadingVersions.value = true;
  try {
    const { data } = await getSectionVersions({ sectionKey, type, qualifier });
    versions.value = data.versions || [];
    if (versions.value.length >= 2) {
      selectedA.value = versions.value[versions.value.length - 2].versionId;
      selectedB.value = versions.value[versions.value.length - 1].versionId;
      await compare();
    }
  } catch (loadError) {
    error.value = loadError?.message || "Failed to load section versions.";
  } finally {
    loadingVersions.value = false;
  }
});

async function compare() {
  if (!canCompare.value) return;
  comparing.value = true;
  error.value = "";
  result.value = null;
  try {
    const { data } = await compareRegSections({ versionIdA: selectedA.value, versionIdB: selectedB.value });
    result.value = data;
    diff.value = buildDiff(data.a.paragraphs, data.b.paragraphs);
  } catch (compareError) {
    error.value = compareError?.message || "Comparison failed.";
  } finally {
    comparing.value = false;
  }
}
</script>
